import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { fileHref, type MeetingReview } from '../api';
import { formatClock } from '../clock';
import styles from './Player.module.css';

export interface PlayerHandle {
  seek: (seconds: number) => void;
}

/**
 * The recording of Beratung 13/15: HTML video on `GET /files/:id` with the drawn overlay (play, progress,
 * „29:40 / 42:18") and — on the desktop — the chapter pills.
 */
export const Player = forwardRef<
  PlayerHandle,
  {
    review: MeetingReview;
    compact?: boolean;
    showChapters?: boolean;
    onTime?: (seconds: number) => void;
  }
>(function Player({ review, compact = false, showChapters = true, onTime }, ref) {
  const video = useRef<HTMLVideoElement>(null);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState<number>(review.recording?.durationSeconds ?? 0);
  const recording = review.recording;

  useImperativeHandle(ref, () => ({
    seek: (seconds: number) => {
      const element = video.current;
      if (!element) return;
      element.currentTime = seconds;
      setTime(seconds);
      onTime?.(seconds);
    },
  }));

  useEffect(() => {
    setDuration(review.recording?.durationSeconds ?? 0);
  }, [review.recording?.durationSeconds]);

  const activeChapter = [...review.chapters].reverse().find((chapter) => chapter.timeSeconds <= time) ?? null;
  const progress = duration > 0 ? Math.min(100, (time / duration) * 100) : 0;

  function toggle() {
    const element = video.current;
    if (!element) return;
    if (element.paused) void element.play().catch(() => undefined);
    else element.pause();
  }

  return (
    <div className={clsx(styles.player, compact && styles.compact)}>
      {recording ? (
        <video
          ref={video}
          className={styles.video}
          src={fileHref(recording.fileId)}
          preload="metadata"
          playsInline
          onTimeUpdate={(event) => {
            const seconds = event.currentTarget.currentTime;
            setTime(seconds);
            onTime?.(seconds);
          }}
          onLoadedMetadata={(event) => {
            const seconds = event.currentTarget.duration;
            if (Number.isFinite(seconds) && seconds > 0) setDuration(seconds);
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
      ) : (
        <div className={styles.empty}>Keine Aufzeichnung vorhanden.</div>
      )}
      {recording ? (
        <div className={styles.overlay}>
          <div className={styles.row}>
            <button
              type="button"
              className={styles.play}
              aria-label={playing ? 'Pause' : 'Abspielen'}
              onClick={toggle}
            >
              {playing ? <Pause size={14} strokeWidth={2} /> : <Play size={14} strokeWidth={2} />}
            </button>
            <input
              type="range"
              className={styles.progress}
              aria-label="Position in der Aufzeichnung"
              min={0}
              max={Math.max(1, Math.floor(duration))}
              step={1}
              value={Math.floor(time)}
              aria-valuetext={`${formatClock(time)} von ${formatClock(duration)}`}
              style={{ ['--progress' as string]: `${progress}%` }}
              onChange={(event) => {
                const seconds = Number(event.target.value);
                if (video.current) video.current.currentTime = seconds;
                setTime(seconds);
              }}
            />
            <span className={styles.time}>
              {formatClock(time)} / {formatClock(duration)}
            </span>
          </div>
          {showChapters && review.chapters.length > 0 ? (
            <div className={styles.chapters} role="group" aria-label="Kapitel">
              {review.chapters.map((chapter) => (
                <button
                  key={chapter.timeSeconds}
                  type="button"
                  className={clsx(styles.chapter, activeChapter === chapter && styles.chapterActive)}
                  aria-current={activeChapter === chapter ? 'true' : undefined}
                  onClick={() => {
                    if (video.current) video.current.currentTime = chapter.timeSeconds;
                    setTime(chapter.timeSeconds);
                    onTime?.(chapter.timeSeconds);
                  }}
                >
                  {chapter.timeLabel} {chapter.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});
